import { useCallback, useState } from "react";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectCouponCode } from "../store/purchasesSelectors";
import { clearCouponCode, setCouponCode } from "../store/purchasesSlice";
import type {
  PackageTypeRouteT,
  PeriodTypeT,
  PurchaseParamsT,
} from "../models/models";
import { validateCouponCode } from "../store/purchasesThunks";
import { useParams } from "react-router-dom";
import { createTransactionFreeCoupon } from "../../Purchases/APICalls";
import { useUser } from "../../../../contexts/userContext/userContext";
import usePurchases from "./usePurchases";
import getPriceWithDiscount from "../components/PurchaseCouponSection/helpers/getPriceWithDiscount";

/**
 *
 * Upgrade subscription with promotion (coupon) code
 *
 * In the upgrade process, no matter which of the options as described so far, if the user has a promotion code,
 * the discount of the promotion code will be applied to the new subscription price. But the calculation of the
 * discount will be done only after reducing the sum of the unused time from the current subscription.
 *
 * Let’s take for example a Pro yearly subscriber who after 6 months of subscription wants to upgrade to Business.
 * And let’s assume for this example that the user has a 30% discount promotion code.
 *
 * The calculation of the price that the user will be required to pay will be as follows
 *
 * (NEW_SUBSCRIPTION_PRICE - SUM_OF_UNUSED_DAYS) X DISCOUNT%
 *
 * NEW_SUBSCRIPTION_PRICE = 790 ILS
 * SUM_OF_UNUSED_DAYS = 210 ILS (6 months price of JUMBOpro subscription)
 * 790-210 = 580 ILS
 * 580 * 30% Discount = 174 ILS
 *
 * The final price to pay is = 580 - 174 = 406 ILS
 *
 */

const useCouponCode = () => {
  const { packageType, periodType } = useParams<PurchaseParamsT>();
  const dispatch = useAppDispatch();
  const { selectedPlanPrice } = usePurchases();
  const { getLanguageCulture } = useContent();
  const { getUserData, isLoggedIn } = useUser();
  const couponCode = useAppSelector(selectCouponCode);
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);

  const updateCouponCodeValue = useCallback(
    (value: string) => {
      dispatch(setCouponCode(value));
    },
    [dispatch]
  );

  const validateCoupon = useCallback(
    (periodType: PeriodTypeT) => {
      const payload = {
        CouponCurrentCulture: getLanguageCulture(),
        PackageName: packageType as PackageTypeRouteT,
        periodType,
      };

      dispatch(validateCouponCode(payload));
    },
    [dispatch, getLanguageCulture(), packageType]
  );

  const handleCreateTransactionFreeCoupon = useCallback(async () => {
    createTransactionFreeCoupon({
      setLoadingProccess: setIsCreatingTransaction,
      selectedPurchase: {
        packageType,
        periodType,
        systemName: packageType,
      },
      isLoggedIn: isLoggedIn(),
      resValidCoupon: couponCode?.code
        ? {
            couponCode: couponCode.code,
          }
        : null,
    });
  }, [couponCode.code, isLoggedIn(), packageType, periodType]);

  const selectedPlanPriceWithDiscount = getPriceWithDiscount(
    selectedPlanPrice!,
    couponCode?.discount || 0
  );

  const clearCouponCodeData = useCallback(() => {
    dispatch(clearCouponCode());
  }, [dispatch]);

  return {
    couponCode,
    setCouponCode: updateCouponCodeValue,
    validateCoupon,
    createTransactionFreeCoupon: handleCreateTransactionFreeCoupon,
    isCreatingTransaction,
    selectedPlanPriceWithDiscount,
    clearCouponCodeData,
  };
};

export default useCouponCode;
